/** @file The light bundle validator runtimes run on load (R4): shapes, references, rects, files, hashes. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { validateClipAgainstPuppet } from '@zilo/pose3d-format/validate/validateClipAgainstPuppet';
import type { BundleError } from '@zilo/pose3d-export/check/bundleError';
import { checkBundleDrawables } from '@zilo/pose3d-export/check/checkBundleDrawables';
import { checkBundleFiles } from '@zilo/pose3d-export/check/checkBundleFiles';
import { checkBundleTables } from '@zilo/pose3d-export/check/checkBundleTables';
import { checkBundleNodes } from '@zilo/pose3d-export/check/checkBundleNodes';
import { checkManifest } from '@zilo/pose3d-export/check/checkManifest';
import type { BundleManifest, ExportPuppet } from '@zilo/pose3d-export/types/bundle';

type Obj = Record<string, unknown>;
type Result = { ok: boolean; errors: BundleError[]; warnings: Warning[] };

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const format = (path: string, message: string): BundleError => ({ code: 'BUNDLE_FORMAT', path, message });
const why = (e: unknown): string => (isObj(e) && typeof e.message === 'string' ? e.message : String(e));

const PUPPET_FIELDS: [string, (v: unknown) => boolean][] = [
  ['nodes', isObj], ['sockets', isObj], ['sheets', isObj], ['tables', isObj], ['drawables', Array.isArray], ['attachments', Array.isArray],
];

function manifestOf(m: unknown, errors: BundleError[]): BundleManifest | undefined {
  try {
    return checkManifest(m);
  } catch (e) {
    const path = isObj(e) && typeof e.path === 'string' && e.path !== '/' ? e.path : '';
    errors.push(format(`/manifest${path}`, why(e)));
    return undefined;
  }
}

/** Errors of the puppet; false when its shape is too wrong to check its clips against. */
function checkPuppet(puppet: Obj, errors: BundleError[]): boolean {
  if (puppet.format !== 'pose3d/puppet-bundle@1') { errors.push(format('/puppet/format', "expected 'pose3d/puppet-bundle@1'")); return false; }
  const bad = PUPPET_FIELDS.find(([key, ok]) => !ok(puppet[key]));
  if (bad !== undefined) { errors.push(format(`/puppet/${bad[0]}`, `puppet.${bad[0]} has the wrong shape`)); return false; }
  const p = puppet as ExportPuppet;
  try {
    errors.push(...checkBundleNodes(p));
    const tables = checkBundleTables(p);
    errors.push(...tables.errors);
    errors.push(...checkBundleDrawables(p, tables.bad));
    return true;
  } catch (e) {
    errors.push(format('/puppet', `unreadable puppet: ${why(e)}`));
    return false;
  }
}

function checkClips(bundle: Obj, puppet: ExportPuppet, manifest: BundleManifest | undefined, result: Result): void {
  const clips = bundle.clips as Record<string, unknown>;
  for (const name of Object.keys(manifest?.clips ?? {})) if (!Object.hasOwn(clips, name)) result.errors.push(format(`/clips/${name}`, `clip ${name} is missing`));
  const host = { nodes: puppet.nodes, name: puppet.name } as Puppet;
  for (const [name, clip] of Object.entries(clips)) {
    try {
      const warnings = validateClipAgainstPuppet(clip as Clip, host);
      result.warnings.push(...warnings.map((w) => ({ ...w, path: `/clips/${name}${w.path ?? ''}` })));
    } catch (e) {
      result.errors.push(format(`/clips/${name}`, `unreadable clip: ${why(e)}`));
    }
  }
}

/**
 * Checks `bundle` without `validatePuppet`: the manifest (`checkManifest`), the puppet format and shape, node
 * parents, sockets and attachment fields, table shapes, drawable nodes, entry tables, rect counts, sheets and bounds; with
 * `opts.files`, every manifest file's presence, byte count and hash (`opts.hash`, or fnv1a64 for fnv1a64 bundles).
 * A clip naming a node the puppet lacks is a warning (`UNKNOWN_NODE_IN_CLIP`). Never throws.
 */
export function checkBundle(bundle: unknown, opts: { files?: Record<string, Uint8Array | string>; hash?: (bytes: Uint8Array) => string } = {}): Result {
  const result: Result = { ok: false, errors: [], warnings: [] };
  if (!isObj(bundle) || !isObj(bundle.puppet) || !isObj(bundle.clips) || !isObj(bundle.sheets)) {
    result.errors.push(format('/', 'expected { manifest, puppet, clips, sheets }'));
    return result;
  }
  const manifest = manifestOf(bundle.manifest, result.errors);
  if (checkPuppet(bundle.puppet, result.errors)) checkClips(bundle, bundle.puppet as ExportPuppet, manifest, result);
  if (manifest !== undefined && opts.files !== undefined) result.errors.push(...checkBundleFiles(manifest, opts.files, opts.hash));
  result.ok = result.errors.length === 0;
  return result;
}
